import React, { useRef, useState, useEffect } from 'react';

// CustomInput component: A text input with a clear 'x' icon.
// It functions as an uncontrolled component, using defaultValue.
// Now accepts an 'onChange' prop to pass to the internal input.
const CustomInput = ({ defaultValue = '', placeholder = '', onChange, disabled, inputId, locationChanged, ...inputProps }) => {
    // useRef to get a direct reference to the input DOM element.
    // This is essential for uncontrolled components as we directly manipulate the DOM.
    const inputRef = useRef(null);

    // useState to manage the visibility of the clear icon.
    // The icon is shown only when there is text in the input.
    const [hasValue, setHasValue] = useState(false);

    // useEffect to initialize hasValue based on defaultValue when the component mounts.
    // This ensures the 'x' icon is visible if a defaultValue is present initially.
    useEffect(() => {
        if (inputRef.current) {
            setHasValue(!!inputRef.current.value);
        }
    }, []); // Empty dependency array ensures this runs only once on mount

    function enableEnterKey(input) {

        /* Store original event listener */
        const _addEventListener = input.addEventListener

        const addEventListenerWrapper = (type, listener) => {
            if (type === 'keydown') {
                /* Store existing listener function */
                const _listener = listener
                listener = (event) => {
                    /* Simulate a 'down arrow' keypress if no address has been selected */
                    const suggestionSelected = document.getElementsByClassName('pac-item-selected').length
                    if ((event.key === 'Enter' || event.key === 'Tab') && !suggestionSelected) {
                        const e = new KeyboardEvent('keydown', {
                            key: 'ArrowDown',
                            code: 'ArrowDown',
                            keyCode: 40
                        } as any)
                        _listener.apply(input, [e])
                    }
                    _listener.apply(input, [event])
                }
            }
            _addEventListener.apply(input, [type, listener])
        }

        input.addEventListener = addEventListenerWrapper
    }

    function enableAutoSelectFirst(input: HTMLInputElement) {
        const autoSelectFirstEntry = (e) => {
            if (e.type === 'keydown' && e.keyCode === 13) e.preventDefault()

            const isSuggestionSelected =
                document.querySelectorAll('.pac-item-selected').length > 0 ||
                document.querySelectorAll('.pac-container .pac-item:hover').length > 0

            if (
                !isSuggestionSelected &&
                ((e.keyCode === 13 && !e.triggered) || e.type === 'blur')
            ) {
                let arrowDownKeyEvent = new KeyboardEvent('keydown', {
                    bubbles: true,
                    cancelable: true,
                    key: 'Down',
                    code: 'Down',
                    keyCode: 40,
                    which: 40,
                } as any);
                google.maps.event.trigger(e.target, 'keydown', arrowDownKeyEvent);

                const enterKeyEvent = new KeyboardEvent('keydown', {
                    bubbles: true,
                    cancelable: true,
                    key: 'Enter',
                    code: 'Enter',
                    keyCode: 13,
                    which: 13,
                    triggered: true,
                } as any)
                google.maps.event.trigger(e.target, 'keydown', enterKeyEvent);
            }
        }

        google.maps.event.addDomListener(input, 'blur', autoSelectFirstEntry);
        google.maps.event.addDomListener(input, 'keydown', autoSelectFirstEntry);
    }

    useEffect(() => {
        var input = document.getElementById('zipcity') as HTMLInputElement;
        /*
        if (!window.location.search) {
            document.getElementById('zipcity').focus();
        }*/

        //input.value = zip || '';

        const options = {
            types: ['(regions)']
            //componentRestrictions: { country: 'us' },
        };

        (window as any).autocomplete = new (window as any).google.maps.places.Autocomplete(input, options);
        var autocomplete = (window as any).autocomplete;
        autocomplete.unbind("bounds");
        autocomplete.setFields(
            ['address_components', 'geometry', 'name']);
        autocomplete.setOptions({ strictBounds: true });
        autocomplete.setBounds({ east: -64.96, west: -72.6, north: 43.135, south: 40.9453 });
        //enableEnterKey(input);
        enableAutoSelectFirst(input);
        //var button = document.getElementById('searchButton') as HTMLButtonElement;
        let currentPlace = new URLSearchParams(window.location.search).get('place') || '';

        autocomplete.addListener('place_changed', async () => {
            const place = autocomplete.getPlace();            
            if (place && place.geometry) {                
                let zipcity = document.getElementById('zipcity') as HTMLInputElement;
                let placeName = zipcity.value.replace(", USA", "");
                zipcity.value = placeName;

                if (currentPlace === placeName) {
                    return;
                }
                
                locationChanged({ lat: place.geometry.location.lat(), lng: place.geometry.location.lng(), name: zipcity.value });
            }
            //history.push(`/search?lat=${place.geometry.location.lat()}&lng=${place.geometry.location.lng()}&place=${encodeURIComponent(input.value)}`);
        });        
    }, [/*window.location.search*/]);

    // handleClear: Function to clear the input's content.
    const handleClear = () => {
        if (inputRef.current) {
            inputRef.current.value = ''; // Directly clear the input value in the DOM
            setHasValue(false); // Hide the clear icon
            inputRef.current.focus(); // Optionally, focus the input after clearing
            // If an onChange handler is provided, call it with an empty event
            // to signal that the value has been cleared.
            if (onChange) {
                onChange({ target: inputRef.current });
            }
        }
    };

    // handleChange: Function to update the hasValue state based on input content.
    // This makes the 'x' icon appear or disappear as the user types.
    // It also calls the passed-in onChange prop.
    const handleChange = (event) => { // Added event parameter
        if (inputRef.current) {
            setHasValue(!!inputRef.current.value); // Update hasValue based on current input value
        }
        // Call the parent's onChange handler if it was provided
        if (onChange) {
            onChange(event);
        }
    };


    return (
        // Bootstrap classes for the container: position-relative for absolute positioning of button,
        // d-flex and align-items-center for flex layout, border and rounded for styling.
        // Custom style for focus-within effect as Bootstrap doesn't have a direct utility for it.
        <div className="position-relative d-flex align-items-center border rounded custom-input-container-bootstrap" style={{ borderColor: "var(--primary-color) !important", backgroundColor: disabled ? "#e9ecef" : null }} >
            <input
                type="text"
                ref={inputRef} // Assign the ref to the input element
                defaultValue={defaultValue} // Set the initial value for this uncontrolled component
                placeholder={placeholder}
                onChange={handleChange} // Listen for changes to manage the clear icon visibility and pass to parent                
                disabled={disabled}
                id={inputId}
                // Bootstrap form-control for input styling, border-0 and shadow-none to remove default input styles,
                // bg-transparent for background, pe-5 for padding on the right to make space for the icon.
                className="form-control border-0 shadow-none bg-transparent pe-5"                
                {...inputProps}
            />            
            <button
                type="button"
                tabIndex={2}
                onClick={handleClear}
                // Bootstrap classes for the clear button: position-absolute for positioning,
                // end-0 for right alignment, p-1 for padding, btn btn-link for basic button styling,
                // text-muted for color.
                className="end-0 p-1 btn btn-link text-muted"
                aria-label="Clear input"
                style={{ visibility: hasValue ? "visible" : "hidden" }}
            >
                {/* Using an SVG icon for the clear icon */}
                <svg className="bi bi-x-lg" fill="currentColor" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg" style={{ width: '1em', height: '1em' }}>
                    <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8z" />
                </svg>
            </button>
            
            {/* Custom CSS for focus-within effect */}
            <style>{`
        .custom-input-container-bootstrap:focus-within {
          border-color: #86b7fe; /* Bootstrap primary blue */
          box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25); /* Bootstrap focus ring shadow */
        }
        .custom-input-container-bootstrap .form-control:focus {
          box-shadow: none; /* Remove default form-control focus shadow to avoid double shadow */
        }
      `}</style>
        </div>
    );
};

export default CustomInput;